import Vue from 'vue';
import Router from 'vue-router';
import { LANG, LangValues } from '@/const/Lang';

const ErrorPage = () => import(/* webpackChunkName: "error-page" */ '@/view/pages/ErrorPage/ErrorPage.vue');

const GuideMapTopPage = () => import(/* webpackChunkName: "guidemap-top" */ '@/view/pages/GuideMapPage/GuideMapTopPage.vue');
const GuideMapFramePage = () => import(/* webpackChunkName: "guidemap-frame" */ '@/view/pages/GuideMapPage/GuideMapFramePage.vue');
const GuideMapPrintPage = () => import(/* webpackChunkName: "guidemap-print" */ '@/view/pages/GuideMapPage/pc/GuideMapPrintPage.vue');

Vue.use(Router);

// 新しい URL 構造: /:siteId/:lang/{mode}
// 地図TOPは /:siteId/:lang/guide-map。list 等の各モードは言語pathの直後に並ぶ（guide-map配下にはしない）。
// 旧 URL 構造 (/town-guide/:lang/:categoryName/...) は下記の redirect ルートで
// SPA 内 (フロントエンド) で 301 相当のリダイレクトを行い、新URLへ移行する。
const guidemapBasePath = '/:siteId/:lang';

const routesList = [
  // ---- 旧URL→新URL 互換リダイレクト（ブックマーク救済用） ----
  // 旧URL構造 `/town-guide/:lang/:categoryName/:rest*` を
  // 新URL構造 `/:categoryName/:lang/:rest*` へフロントエンドで redirect する。
  // 'town-guide' が消えて categoryName が siteId になり、言語pathの位置が前へ移るだけ。
  // rest（guide-map / list など）はそのまま保持する。クエリストリングも保持。
  // 例: /town-guide/ja/mojiko-sanpo/guide-map/ → /mojiko-sanpo/ja/guide-map/
  // 例: /town-guide/ja/mojiko-sanpo/list/      → /mojiko-sanpo/ja/list/
  // ※ 'town-guide' は固定セグメントのため、汎用 :siteId ルートより前に置く必要がある。
  {
    path: '/town-guide/:lang/:categoryName/:rest(.*)?',
    redirect: (to) => {
      const rest = to.params.rest || '';
      // rest が無い場合は地図TOP（guide-map）へ
      const normalizedRest = rest ? `/${rest}` : '/guide-map';
      return {
        path: `/${to.params.categoryName}/${to.params.lang}${normalizedRest}`,
        query: to.query,
      };
    },
  },
  // 城崎温泉マップ: siteId を kinosakionsen-architecture-design → kinosaki-architecture-design へ正規化
  // merengue の SID 文字数上限(32文字)対応のため短い ID に変更。旧 URL は新 URL へリダイレクト。
  {
    path: '/kinosakionsen-architecture-design/:lang/:rest(.*)?',
    redirect: (to) => {
      const rest = to.params.rest || '';
      const normalizedRest = rest ? `/${rest}` : '/guide-map';
      return {
        path: `/kinosaki-architecture-design/${to.params.lang}${normalizedRest}`,
        query: to.query,
      };
    },
  },
  {
    // ガイドマップ TOP（地図画面）
    path: `${guidemapBasePath}/guide-map`,
    name: 'GuideMapTopPage',
    component: GuideMapTopPage,
  },
  {
    // ガイドマップ 印刷ページ
    path: `${guidemapBasePath}/print`,
    name: 'GuideMapPrintPage',
    component: GuideMapPrintPage,
  },
  {
    // ガイドマップ TOP（フレーム内表示）
    path: `${guidemapBasePath}/frame`,
    name: 'GuideMapFramePage',
    component: GuideMapFramePage,
  },
  {
    // ガイドマップ モード選択（フレーム内表示）
    path: `${guidemapBasePath}/frame/:mode/:code`,
    name: 'GuideMapFramePage',
    component: GuideMapFramePage,
  },
  {
    // ガイドマップ モード選択（list/route/course/word/specify 等 + コード）
    path: `${guidemapBasePath}/:mode/:code`,
    name: 'GuideMapTopPage',
    component: GuideMapTopPage,
  },
  {
    // ガイドマップ モード（list/route/course/word/specify 等）
    path: `${guidemapBasePath}/:mode`,
    name: 'GuideMapTopPage',
    component: GuideMapTopPage,
  },

  // ---- 画面を追加する場合はこの上に設定する ----
  // ---- 以下、リダイレクト・エラー設定 ----
  {
    // エラー画面（push({ name: 'ErrorPage' }) でのナビゲーション用）
    path: '/error',
    name: 'ErrorPage',
    component: ErrorPage,
  },
  {
    // キャッチオール（存在しないパスへのアクセス用）
    path: '*',
    component: ErrorPage,
  },
  // ---- 画面を追加する場合はリダイレクト・エラー設定より上に設定する ----
];

/**
 * router 初期化
 */
const router = new Router({
  mode: 'history',
  routes: routesList,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
  },
});

router.beforeEach((to, from, next) => {
  // 開発環境ではすべてのチェックをスキップ
  if (process.env.NODE_ENV === 'development') {
    next(true);
    return;
  }
  // URLにaccess-deniedが含まれていたらNICHER TRAVELのページにリダイレクト
  // ※ /access-denied は * catchall にマッチし to.name が null になるため、!to.name チェックより前に判定する
  if (to.fullPath === '/access-denied') {
    const redirectUrl = `${process.env.SITE.NICHER_TRAVEL}/account/access-denied`;
    window.location.href = redirectUrl;
    return;
  }
  if (!to.name) {
    // 存在しない言語が指定された場合は、日本語でリダイレクト
    let lang = LangValues.find((lang) => { return to.params.lang === lang.path; });
    if (!lang) {
      const queryLang = LangValues.find((lang) => { return to.query.lang === lang.path; });
      lang = !queryLang ? LANG.ja : queryLang.path;
      next({ name: to.name, params: { lang: lang, siteId: to.params.siteId}, query: to.query });
    } else {
      next(true);
    }
    return;
  }

  // ログイン済みで居住地の入力が無い場合は本登録未完了と見なし、本登録画面にリダイレクトさせる
  if (sessionStorage.getItem("registrationChecked")) {
    next();
    return;
  }
  // Memberstackチェック処理（タイムアウト付き）
  const memberstack = window.$memberstackDom;
  if (!memberstack) {
    next(true);
    return;
  }

  const MEMBERSTACK_TIMEOUT_MS = 3000;
  const timeoutPromise = new Promise((resolve) => {
    setTimeout(() => resolve({ timeout: true }), MEMBERSTACK_TIMEOUT_MS);
  });

  Promise.race([
    memberstack.getCurrentMember(),
    timeoutPromise,
  ]).then(result => {
    // タイムアウト時はページ遷移をブロックせず通過（次回遷移時に再チェック）
    if (result && result.timeout) {
      console.warn("Memberstack getCurrentMember timed out");
      next(true);
      return;
    }
    const member = result;
    // ログインしていない場合、member.dataが存在しない
    if (!member || !member.data) {
      next(true);
      return;
    }
    const customFields = member.data.customFields;
    if (!customFields || !customFields.residence || customFields.residence.trim() === "") {
      const registrationUrl = `${process.env.SITE.NICHER_TRAVEL}/account/registration`;
      window.location.href = registrationUrl;
      return;
    }
    // 同一セッション内で再確認しないようフラグをセット
    sessionStorage.setItem("registrationChecked", "true");
    next(true);
  }).catch(error => {
    console.error("Memberstack error:", error);
    next(true);
  });
});

export { router as default };
